import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Card, CardContent, Chip, Grid, Stack, ThemeProvider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import UploadIcon from "@mui/icons-material/Upload";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { toast } from "react-toastify";

import PageHeader from "./components/PageHeader";
import OpdReferredRail, { referralSource } from "./components/OpdReferredRail";
import AbhaPanel from "./components/AbhaPanel";
import SectionAccordion from "./components/SectionAccordion";
import ActionsBar from "./components/ActionsBar";
import LoadingOverlay from "./components/LoadingOverlay";

import PersonalInfo from "./components/sections/PersonalInfo";
import Documents from "./components/sections/Documents";
import Contacts from "./components/sections/Contacts";
import Addresses from "./components/sections/Addresses";
import FamilyDetails from "./components/sections/FamilyDetails";
import Emergencies from "./components/sections/Emergencies";
import Guarantors from "./components/sections/Guarantors";
import Insurance from "./components/sections/Insurance";
import Admission from "./components/sections/Admission";
import PatientSourceSection from "./components/sections/PatientSourceSection";
import BedGrid from "./components/sections/BedGrid";

import SearchPatientDialog from "./components/dialogs/SearchPatientDialog";
import CrossDeviceQrDialog from "./components/dialogs/CrossDeviceQrDialog";
import ScanDocumentDialog from "./components/dialogs/ScanDocumentDialog";
import ConsentFormDialog from "./components/dialogs/ConsentFormDialog";
import SmartCardDialog from "./components/dialogs/SmartCardDialog";

import { SectionConfigProvider, useSectionConfig } from "./context/SectionConfigContext";
import { ConsentFormProvider } from "./context/ConsentFormContext";
import { usePatientForm } from "./hooks/usePatientForm";
import { useOpdReferrals } from "./hooks/useOpdReferrals";
import { useStatesDistricts } from "./hooks/useStatesDistricts";
import { EMPTY_PATIENT, SEED_REF_DOCTORS } from "./constants/registrationConstants";
import { validatePatientSubmit, isSeniorCitizen } from "./utils/validation";
import { submitIpdPatientRegistration, mapReferralToPatient } from "./api/registrationService";
import { applyDocumentAutofillToPatient } from "./utils/documentAutofill";
import { ADMISSION_COLORS, createAdmissionTheme } from "./constants/admissionTheme";
import { ensureMinimumListEntries, CONFIGURED_SECTION_IDS, mapErrorsToSectionIds, UI_SECTION_IDS } from "./utils/sectionConfiguration";

const S = CONFIGURED_SECTION_IDS;

function PatientAdmissionIpdContent() {
  const theme = useMemo(() => createAdmissionTheme(), []);
  const { config, isSectionActive } = useSectionConfig();
  const {
    patient, dispatch, setPatient, resetPatient, updateField, updateEncounter,
    updateSource, updateListItem, addListItem, removeListItem, mergePatient,
  } = usePatientForm(EMPTY_PATIENT);

  const [railOpen, setRailOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [smartCardOpen, setSmartCardOpen] = useState(false);
  const [refDoctors, setRefDoctors] = useState(SEED_REF_DOCTORS);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedOpdReferralId, setSelectedOpdReferralId] = useState(null);
  const [selectedOpdReferral, setSelectedOpdReferral] = useState(null);
  const [selectedPatientIdForSearch, setSelectedPatientIdForSearch] = useState(null);
  const [registeredPatientId, setRegisteredPatientId] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const {
    referrals, loading: referralsLoading, error: referralsError, refresh: refreshReferrals,
  } = useOpdReferrals();

  const { states, districtsMap, fetchDistricts } = useStatesDistricts();

  const resetFormState = useCallback(() => {
    dispatch({
      type: "SET_FN",
      updater: () => {
        const base = JSON.parse(JSON.stringify(EMPTY_PATIENT));
        return config ? ensureMinimumListEntries(base, config) : base;
      },
    });
    setSelectedOpdReferralId(null);
    setSelectedOpdReferral(null);
    setSelectedPatientIdForSearch(null);
    setRegisteredPatientId(null);
    setSavedAddresses([]);
    setFieldErrors({});
  }, [config, dispatch]);

  useEffect(() => {
    if (!config) return;
    dispatch({
      type: "SET_FN",
      updater: (current) => ensureMinimumListEntries(current, config),
    });
  }, [config, dispatch]);

  const senior = isSeniorCitizen(patient.dob);

  const sectionsWithErrors = useMemo(
    () => mapErrorsToSectionIds(fieldErrors, config),
    [fieldErrors, config],
  );

  const accumulateSavedAddresses = useCallback((addresses = []) => {
    setSavedAddresses((prev) => {
      const seen = new Set(prev.map((a) => `${a.line1}|${a.pincode}`));
      const merged = [...prev];
      addresses.forEach((a) => {
        const k = `${a.line1}|${a.pincode}`;
        if (a.line1 && !seen.has(k)) {
          merged.push(a);
          seen.add(k);
        }
      });
      return merged.slice(-10);
    });
  }, []);

  const handleDocumentAutofill = useCallback((documentJson) => {
    if (!documentJson || typeof documentJson !== "object") return;
    setPatient((current) => {
      const next = applyDocumentAutofillToPatient(current, documentJson);
      if (next.addresses?.some((a) => a.line1)) {
        accumulateSavedAddresses(next.addresses);
      }
      return next;
    });
  }, [setPatient, accumulateSavedAddresses]);

  const handleSearchOpen = useCallback(() => {
    resetFormState();
    setSearchOpen(true);
  }, [resetFormState]);

  const handleSearchViewPatient = useCallback((payload) => {
    const mapped = payload?.patient;
    if (!mapped) {
      toast.error("Patient not found");
      return;
    }

    const patientId = payload.patient_id || mapped.patient_id || mapped.uhid || null;
    setSelectedPatientIdForSearch(patientId);
    setRegisteredPatientId(patientId);
    setSelectedOpdReferralId(null);

    mergePatient({
      ...EMPTY_PATIENT,
      ...mapped,
      contacts: mapped.contacts?.length ? mapped.contacts : EMPTY_PATIENT.contacts,
      addresses: mapped.addresses?.length ? mapped.addresses : EMPTY_PATIENT.addresses,
      emergencyContacts: mapped.emergencyContacts?.length
        ? mapped.emergencyContacts
        : EMPTY_PATIENT.emergencyContacts,
      family: mapped.family || [],
      guarantors: mapped.guarantors || [],
      documents: mapped.documents || [],
      source: { ...EMPTY_PATIENT.source, ...(mapped.source || {}) },
      encounter: { ...EMPTY_PATIENT.encounter, ...(mapped.encounter || {}) },
    });
    if (mapped.addresses?.length) accumulateSavedAddresses(mapped.addresses);

    setSearchOpen(false);
    toast.success(`Patient loaded: ${mapped.name || patientId}`);
  }, [mergePatient, accumulateSavedAddresses]);

  const handleSearchDone = useCallback(() => {
    resetFormState();
    setSearchOpen(false);
  }, [resetFormState]);

  const handleOpdPick = useCallback((referral) => {
    setSelectedPatientIdForSearch(null);
    const referralId = referral.patient_id || referral.id;
    setSelectedOpdReferralId(referralId || null);
    setSelectedOpdReferral(referral);
    if (referralId) setRegisteredPatientId(referralId);
    const mapped = mapReferralToPatient(referral);
    mergePatient({
      ...EMPTY_PATIENT,
      ...mapped,
      contacts: mapped.contacts || EMPTY_PATIENT.contacts,
      addresses: mapped.addresses || EMPTY_PATIENT.addresses,
      encounter: { ...EMPTY_PATIENT.encounter, ...mapped.encounter },
    });
    if (mapped.addresses) accumulateSavedAddresses(mapped.addresses);
    toast.info(`Loaded ${referral.name} from ${referralSource(referral)} referrals`);
  }, [mergePatient, accumulateSavedAddresses]);

  const handleSubmit = useCallback(async () => {
    const { valid, errors } = validatePatientSubmit(patient, config);
    setFieldErrors(errors);
    if (!valid) {
      toast.error("Please fix the highlighted required fields");
      return;
    }

    setSubmitting(true);
    let succeeded = false;
    try {
      const result = await submitIpdPatientRegistration(patient, {
        selectedOpdReferralId,
        selectedPatientIdForSearch,
        selectedOpdReferral,
      });

      if (!mountedRef.current) return;

      const patientId = result?.patient_id || null;
      const uhid = result?.uhid || null;
      const admissionId = result?.admission_id ?? null;

      if (patientId) {
        setRegisteredPatientId(patientId);
        setSelectedPatientIdForSearch(patientId);
      }
      if (uhid || admissionId != null) {
        setPatient((s) => ({
          ...s,
          ...(uhid ? { uhid } : {}),
          ...(admissionId != null && admissionId !== "" ? { admission_id: admissionId } : {}),
        }));
      }

      toast.success(result?.message || "IPD Admission created successfully!");
      succeeded = true;
    } catch (err) {
      if (mountedRef.current) {
        toast.error(err.message || "IPD admission failed due to network or server error.");
      }
    } finally {
      if (mountedRef.current) {
        setSubmitting(false);
      }
    }

    if (succeeded && mountedRef.current) {
      refreshReferrals({ silent: true });
    }
  }, [
    patient,
    config,
    setPatient,
    selectedOpdReferralId,
    selectedPatientIdForSearch,
    selectedOpdReferral,
    refreshReferrals,
  ]);

  const displayPatientId = registeredPatientId || selectedPatientIdForSearch;
  const displayUhid = patient.uhid || "";
  const isExistingAdmission = Boolean(patient.admission_id);

  const pageTitle = displayPatientId || displayUhid
    ? "Update Patient"
    : "IPD Patient Registration/Admission";

  const headerActions = (
    <>
      {senior && (
        <Chip
          label="Senior Citizen"
          size="small"
          sx={{ bgcolor: ADMISSION_COLORS.warning.bg, color: ADMISSION_COLORS.warning.text, fontWeight: 600 }}
        />
      )}
      {displayPatientId && (
        <Chip label={displayPatientId} size="small" color="primary" />
      )}
      {displayUhid && displayUhid !== displayPatientId && (
        <Chip label={displayUhid} size="small" color="primary" variant="outlined" />
      )}
      <Button variant="outlined" size="small" startIcon={<SearchIcon />} onClick={handleSearchOpen}>
        Search / Edit Patient
      </Button>
      <Button variant="outlined" size="small" startIcon={<QrCodeScannerIcon />} onClick={() => setScanOpen(true)}>
        Scan QR
      </Button>
      <Button variant="outlined" size="small" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>
        Upload Document
      </Button>
    </>
  );

  const sectionProps = {
    patient,
    setPatient,
    updateField,
    updateEncounter,
    updateSource,
    updateListItem,
    addListItem,
    removeListItem,
    errors: fieldErrors,
  };

  return (
    <ThemeProvider theme={theme}>
      <Box component="main" sx={{ minHeight: "100%", bgcolor: ADMISSION_COLORS.pageBg }}>
        <PageHeader
          title={pageTitle}
          subtitle="Register, search and admit patients into IPD"
          actions={headerActions}
        />

        <Grid container spacing={2} sx={{ p: { xs: 2, md: 3 } }}>
          <Grid item xs={12} lg={railOpen ? 3 : "auto"}>
            <OpdReferredRail
              open={railOpen}
              onToggle={() => setRailOpen((o) => !o)}
              onPick={handleOpdPick}
              referrals={referrals}
              loading={referralsLoading}
              error={referralsError}
              selectedReferralId={selectedOpdReferralId}
            />
          </Grid>

          <Grid item xs={12} lg={railOpen ? 9 : 11}>
            <Stack spacing={2}>
              <AbhaPanel
                patient={patient}
                updateField={updateField}
                mergePatient={mergePatient}
                resetPatient={resetPatient}
                setSelectedPatientIdForSearch={setSelectedPatientIdForSearch}
                selectedPatientIdForSearch={selectedPatientIdForSearch}
                selectedOpdReferralId={selectedOpdReferralId}
                taggingPatientId={selectedPatientIdForSearch || patient.uhid || undefined}
                accumulateSavedAddresses={accumulateSavedAddresses}
              />

              {isSectionActive(S.personal) && (
                <SectionAccordion id={S.personal} title="Personal Information" hasError={sectionsWithErrors.has(S.personal)}>
                  <PersonalInfo {...sectionProps} errors={fieldErrors} />
                </SectionAccordion>
              )}

              {isSectionActive(S.documents) && (
                <SectionAccordion id={S.documents} title="Patient Documents (Passport · Voter ID · National ID · Iqama · Aadhaar · …)" defaultExpanded={false} hasError={sectionsWithErrors.has(S.documents)}>
                  <Documents {...sectionProps} />
                </SectionAccordion>
              )}

              {isSectionActive(S.contacts) && (
                <SectionAccordion id={S.contacts} title="Contacts (Phone / Email)" defaultExpanded={false} hasError={sectionsWithErrors.has(S.contacts)}>
                  <Contacts {...sectionProps} />
                </SectionAccordion>
              )}

              {isSectionActive(S.addresses) && (
                <SectionAccordion id={S.addresses} title="Address (up to 2)" hasError={sectionsWithErrors.has(S.addresses)}>
                  <Addresses
                    {...sectionProps}
                    savedAddresses={savedAddresses}
                    states={states}
                    districtsMap={districtsMap}
                    fetchDistricts={fetchDistricts}
                  />
                </SectionAccordion>
              )}

              {isSectionActive(S.family) && (
                <SectionAccordion id={S.family} title="Family Details / Next-of-Kin" defaultExpanded={false} hasError={sectionsWithErrors.has(S.family)}>
                  <FamilyDetails {...sectionProps} />
                </SectionAccordion>
              )}

              {isSectionActive(S.emergency) && (
                <SectionAccordion id={S.emergency} title="Emergency Contacts" defaultExpanded={false} hasError={sectionsWithErrors.has(S.emergency)}>
                  <Emergencies {...sectionProps} />
                </SectionAccordion>
              )}

              {isSectionActive(S.guarantors) && (
                <SectionAccordion id={S.guarantors} title="Guarantor" defaultExpanded={false} hasError={sectionsWithErrors.has(S.guarantors)}>
                  <Guarantors {...sectionProps} />
                </SectionAccordion>
              )}

              {isSectionActive(S.finance) && (
                <SectionAccordion id={S.finance} title="Financial Details" defaultExpanded={false} hasError={sectionsWithErrors.has(S.finance)}>
                  <Insurance {...sectionProps} errors={fieldErrors} />
                </SectionAccordion>
              )}

              {isSectionActive(S.source) && (
                <SectionAccordion id={S.source} title="Patient Source" defaultExpanded={false} hasError={sectionsWithErrors.has(S.source)}>
                  <PatientSourceSection
                    patient={patient}
                    updateSource={updateSource}
                    refDoctors={refDoctors}
                    setRefDoctors={setRefDoctors}
                    errors={fieldErrors}
                  />
                </SectionAccordion>
              )}

              <SectionAccordion id="admission" title="Admission · Admitting & Attending Doctor" hasError={sectionsWithErrors.has(UI_SECTION_IDS.admission)}>
                <Admission {...sectionProps} errors={fieldErrors} disabled={isExistingAdmission} />
              </SectionAccordion>

              <BedGrid
                patient={patient}
                setPatient={setPatient}
                errors={fieldErrors}
                disabled={isExistingAdmission}
                hasError={sectionsWithErrors.has(UI_SECTION_IDS.bedAssignment)}
              />

              <Card variant="outlined">
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "stretch", md: "center" }}
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <ActionsBar
                      uhid={patient.uhid}
                      onConsent={() => setConsentOpen(true)}
                      onSmartCard={() => setSmartCardOpen(true)}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button variant="outlined" onClick={resetFormState} disabled={submitting}>
                        Reset
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                        onClick={handleSubmit}
                        disabled={submitting}
                      >
                        {displayPatientId || displayUhid ? "Update Patient" : "Submit Patient"}
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Grid>
        </Grid>

        <SearchPatientDialog
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onDone={handleSearchDone}
          onViewPatient={handleSearchViewPatient}
        />
        <CrossDeviceQrDialog
          open={scanOpen}
          onClose={() => setScanOpen(false)}
          onAutofill={handleDocumentAutofill}
        />
        <ScanDocumentDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          onAutofill={handleDocumentAutofill}
        />
        <ConsentFormDialog
          open={consentOpen}
          onClose={() => setConsentOpen(false)}
          patient={patient}
          patientId={displayPatientId || patient.patient_id}
          updateField={updateField}
        />
        <SmartCardDialog
          open={smartCardOpen}
          onClose={() => setSmartCardOpen(false)}
          patient={patient}
        />

        <LoadingOverlay open={submitting} message="Submitting patient…" />
      </Box>
    </ThemeProvider>
  );
}

function PatientAdmissionIpd() {
  return (
    <SectionConfigProvider>
      <ConsentFormProvider>
        <PatientAdmissionIpdContent />
      </ConsentFormProvider>
    </SectionConfigProvider>
  );
}

export default PatientAdmissionIpd;
